import { Row, Col, Steps, Button, StepProps, message, Form, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RegistrationWidget from 'src/components/dashboard/common-widget/registration-widget/registration-widget';
import { ACCOUNT_TYPE } from 'src/constants/mortgage-deal';

import { PAGE_ROUTE } from 'src/constants/route';
import { scrollPageToTop } from 'src/helpers/window';
import useStore from 'src/hooks/useStore';

import { i18nKey } from 'src/locales/i18n';
import OnBoardingRegisterPage from 'src/pages/protected/onboarding/onboarding';
import { IMortgageDealDetails } from 'src/store/mortgage-deal/mortgage-deal-details.store';
import { IUserStore } from 'src/store/user';
import BorrowerRequestMortgageDeal from '../../mortgage-deal-form/components/borrower-request-mortgage-deal/individual/borrower-request-mortgage-deal';
import InviteGuarantor from '../../mortgage-deal-form/components/invite-guarantor/invite-guarantor';
import MortgageDealDetails from '../../mortgage-deal-form/components/mortgage-deal-details/mortgage-deal-details';
import MortgageDealSupportingDoc from '../../mortgage-deal-form/components/mortgage-deal-supporting-doc/mortgage-deal-supporting-document';

import CorporateBorrowerRequestPage from '../../mortgage-deal-form/components/corporate/corporate-request-form';

import styles from './view-mortgage-deal-form.module.less';

const ViewMortgageDealForm = () => {
	const [loading, setLoading] = useState(false);

	const [step, setStep] = useState(0);

	const [isOpenConfirmBRForm, setOpenConfirmBRForm] = useState(false);
	const [t] = useTranslation();

	const navigator = useNavigate();

	const [docForm] = Form.useForm();

	const mortgageDealDetailsStore: IMortgageDealDetails = useStore('mortgageDealDetails');

	const userStore: IUserStore = useStore('userStore');

	const [searchParams] = useSearchParams();

	const onSaveClick = () => {
		switch (step) {
			case 0:
				setOpenConfirmBRForm(!isOpenConfirmBRForm);
				break;
			case 1:
				setStep(step + 1);
				message.success(t(i18nKey.mortgageDeal.requestForm.message.confirmForm), 1);
				break;
			case 2:
				setStep(step + 1);
				message.success(t(i18nKey.mortgageDeal.requestForm.message.inviteAGuarantor), 1);
				break;

			case 3:
				message.success(t(i18nKey.mortgageDeal.requestForm.supportingDocument.saveDraft));
				navigator(PAGE_ROUTE.DASHBOARD.concat(PAGE_ROUTE.DASHBOARD_MORTGAGE));

				break;
			default:
				break;
		}
	};

	const stepItems: StepProps[] = [
		{
			title: `${t(i18nKey.mortgageDeal.requestForm.steps.borrowerRequest)}`,
			status: 'finish'
		},
		{
			title: `${t(i18nKey.mortgageDeal.requestForm.steps.mortgageDealDetails)}`,
			status: 'finish'
		},
		{
			title: `${t(i18nKey.mortgageDeal.requestForm.steps.inviteGuarantor)}`,
			status: 'finish'
		},
		{
			title: `${t(i18nKey.mortgageDeal.requestForm.steps.supportingDocument)}`,
			status: 'finish'
		}
	];

	const renderStepPage = (step: number) => {
		switch (step) {
			case 0:
				if (userStore.accountTypeMap === ACCOUNT_TYPE.CORPORATE) {
					return <CorporateBorrowerRequestPage onConfirmForm={onSaveClick} isViewFullForm />;
				}

				return <BorrowerRequestMortgageDeal onConfirmForm={onSaveClick} isViewFullForm />;
			case 1:
				return <MortgageDealDetails isViewFullForm />;
			case 2:
				return <InviteGuarantor inviteForm={docForm} isViewFullForm />;
			case 3:
				return <MortgageDealSupportingDoc isViewFullForm form={docForm} />;
			default:
				break;
		}
	};

	const onBack = () => {
		navigator(PAGE_ROUTE.DASHBOARD.concat(PAGE_ROUTE.DASHBOARD_MORTGAGE));
		mortgageDealDetailsStore.dispose();
	};

	useEffect(() => {
		scrollPageToTop();
	}, []);

	useEffect(() => {
		setLoading(true);
		const mortgageId = searchParams.get('id');

		if (mortgageId !== null) {
			mortgageDealDetailsStore
				.getMortgageDealDetails(mortgageId)
				.then(() => {
					setLoading(false);
				})
				.catch();
		} else {
			mortgageDealDetailsStore.dispose();
			navigator(PAGE_ROUTE.DASHBOARD.concat(PAGE_ROUTE.DASHBOARD_MORTGAGE));
		}
	}, []);

	return (
		<Spin spinning={loading}>
			<OnBoardingRegisterPage>
				<RegistrationWidget title={t(i18nKey.mortgageDeal.requestForm.title)}>
					<div className={styles.subLevelForm}>
						<Steps
							current={step}
							onChange={(s) => {
								setStep(s);
							}}
							labelPlacement="vertical"
							items={stepItems}
							responsive
							style={{ justifyContent: 'center' }}
						/>
					</div>
					<div className={styles.marginTop}>{renderStepPage(step)}</div>

					<Row gutter={[16, 16]} className={styles.marginTop}>
						<Col span={24}>
							<Button block type="primary" onClick={onBack}>
								{t(i18nKey.common.back)}
							</Button>
						</Col>
					</Row>
				</RegistrationWidget>
			</OnBoardingRegisterPage>
		</Spin>
	);
};

export default observer(ViewMortgageDealForm);
